import { api } from './api';
import {
  KYCSubmission,
  KYCSubmissionData,
  KYCDocument,
  KYCHistoryEntry,
  PendingKYCResponse,
  KYCReviewRequest,
  KYCDocumentType,
} from '../types/kyc';

export const kycService = {
  // ==================== User Endpoints ====================

  /**
   * Submit KYC application
   * @param userId - User ID
   * @param data - KYC submission data (personal info + address)
   */
  async submitKYC(userId: string, data: KYCSubmissionData): Promise<KYCSubmission> {
    const response = await api.post<KYCSubmission>(`/users/${userId}/kyc`, data);
    return response.data;
  },

  /**
   * Upload document metadata (after uploading file via uploadService)
   * @param userId - User ID
   * @param document - Document metadata
   */
  async uploadDocument(
    userId: string,
    document: {
      document_type: KYCDocumentType;
      file_name: string;
      file_url: string;
      file_size: number;
      mime_type: string;
    }
  ): Promise<KYCDocument> {
    const response = await api.post<KYCDocument>(`/users/${userId}/kyc/documents`, document);
    return response.data;
  },

  /**
   * Get current KYC status and submission details
   * @param userId - User ID
   */
  async getKYCStatus(userId: string): Promise<KYCSubmission | null> {
    try {
      const response = await api.get<KYCSubmission>(`/users/${userId}/kyc`);
      return response.data;
    } catch (error: any) {
      // 404 means no KYC submission yet
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get uploaded documents
   * @param userId - User ID
   */
  async getDocuments(userId: string): Promise<KYCDocument[]> {
    try {
      const response = await api.get<KYCDocument[]>(`/users/${userId}/kyc/documents`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  /**
   * Get KYC history
   * @param userId - User ID
   */
  async getHistory(userId: string): Promise<KYCHistoryEntry[]> {
    try {
      const response = await api.get<KYCHistoryEntry[]>(`/users/${userId}/kyc/history`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  // ==================== Admin Endpoints ====================

  /**
   * Get pending KYC submissions (admin only)
   * @param page - Page number (1-indexed)
   * @param pageSize - Items per page
   */
  async getPendingSubmissions(page: number = 1, pageSize: number = 20): Promise<PendingKYCResponse> {
    const response = await api.get<PendingKYCResponse>('/admin/kyc/pending', {
      params: { page, page_size: pageSize },
    });
    return response.data;
  },

  /**
   * Get submission details (admin only)
   * @param submissionId - KYC submission ID
   */
  async getSubmissionDetails(submissionId: string): Promise<KYCSubmission> {
    const response = await api.get<KYCSubmission>(`/admin/kyc/${submissionId}`);
    return response.data;
  },

  /**
   * Approve or reject KYC submission (admin only)
   * @param userId - User ID
   * @param review - Review action with reason/notes
   */
  async reviewSubmission(userId: string, review: KYCReviewRequest): Promise<KYCSubmission> {
    const response = await api.put<KYCSubmission>(`/admin/kyc/${userId}/review`, review);
    return response.data;
  },
};

export default kycService;
